/* A mobile number field with a country picker.
 *
 * The web half of app/mobile/lib/core/widgets/cn_phone_field.dart, and it
 * fixes the same bug on this side: signup showed a static "+91" and then
 * submitted the bare digits typed beside it. The prefix was decoration. That
 * worked by accident for India — the server defaults a bare number to +91 —
 * and left everyone else unable to register at all, because the form had no
 * way to express any other country.
 *
 * Everything about a phone number lives here now: the picker, the hint, the
 * digit limit, and the one place E.164 is assembled. A page holds a country
 * and the national digits, and calls toE164() when it submits.
 *
 * THE SERVER IS THE REAL GATE. normalize_mobile validates E.164 properly since
 * bbf596c; the digit check here only catches a typo before a round trip.
 */
import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { Field } from '@/components/ui/field'
import { COUNTRIES, searchCountries, type Country } from '@/lib/countries'
import { cn } from '@/lib/utils'

export type PhoneFieldProps = {
  country: Country
  onCountryChange: (c: Country) => void
  /** The NATIONAL digits only — never the country code. Keeping the two apart
   *  is what stops "+91" being typed into a field that already shows it. */
  value: string
  onValueChange: (v: string) => void
  label?: string
  help?: string
  error?: string | null
  required?: boolean
  disabled?: boolean
  autoFocus?: boolean
}

export function PhoneField({
  country,
  onCountryChange,
  value,
  onValueChange,
  label = 'Mobile',
  help = 'We send a 6-digit code here to confirm it is yours',
  error,
  required,
  disabled,
  autoFocus,
}: PhoneFieldProps) {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative">
      <Field
        label={label}
        value={value}
        onChange={(e) =>
          // Digits only, and one more than the country expects so somebody who
          // types an extra digit SEES it and can correct it, rather than the
          // field silently swallowing the keystroke.
          onValueChange(e.target.value.replace(/\D/g, '').slice(0, country.digits + 1))
        }
        inputMode="tel"
        // 'tel-national', not 'tel': the browser must not autofill a full
        // international number into a field that holds national digits only.
        autoComplete="tel-national"
        help={error ? undefined : help}
        error={error}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        prefixWidth={country.dial.length > 3 ? 96 : 86}
        prefixSlot={
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            disabled={disabled}
            className={cn(
              'flex items-center gap-1 rounded-sm text-md font-medium text-ink',
              'transition-colors hover:text-brand disabled:cursor-not-allowed disabled:text-ink-3',
            )}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Country code: ${country.name} ${country.dial}. Change`}
          >
            <span aria-hidden>{country.flag}</span>
            <span>{country.dial}</span>
            <ChevronDown className="h-4 w-4 text-ink-2" aria-hidden />
          </button>
        }
      />

      {open ? (
        <CountryMenu
          current={country}
          onPick={(c) => {
            onCountryChange(c)
            setOpen(false)
            // Trim to the new country's length. Ten Indian digits are not a
            // valid eight-digit Qatari number, and carrying them over submits
            // something nobody typed.
            if (value.length > c.digits + 1) onValueChange(value.slice(0, c.digits + 1))
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  )
}

/** The list. A searchable popover rather than a <select>: twenty rows is more
 *  than a native menu wants to be, and typing "uae" beats scrolling. */
function CountryMenu({
  current,
  onPick,
  onClose,
}: {
  current: Country
  onPick: (c: Country) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const boxRef = useRef<HTMLDivElement>(null)
  const rows = searchCountries(query)

  // Close on a click anywhere else, and on Escape. Without both, the menu
  // stays open behind the rest of the form and swallows the next click.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!boxRef.current?.contains(e.target as Node)) onClose()
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    // Deferred: the click that OPENED the menu is still propagating, and
    // listening immediately would close it in the same tick.
    const t = window.setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    document.addEventListener('keydown', onKey)
    return () => {
      window.clearTimeout(t)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  return (
    <div
      ref={boxRef}
      role="listbox"
      className="absolute left-0 top-[58px] z-30 w-full overflow-hidden rounded-md border border-line-2 bg-surface shadow-2"
    >
      <div className="flex items-center gap-s2 border-b border-line-2 px-s3 py-s2">
        <Search className="h-4 w-4 shrink-0 text-ink-2" aria-hidden />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search country or code"
          aria-label="Search country or code"
          className="w-full bg-transparent text-base outline-none placeholder:text-ink-3"
        />
      </div>

      {rows.length === 0 ? (
        <p className="p-s4 text-base text-ink-2">No country matches that.</p>
      ) : (
        <ul className="max-h-[260px] overflow-y-auto py-s1">
          {rows.map((c) => {
            const on = c.code === current.code
            return (
              <li key={c.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => onPick(c)}
                  className={cn(
                    'flex w-full items-center gap-s3 px-s3 py-s2 text-left text-base',
                    'hover:bg-hover-2',
                    on && 'font-semibold text-brand',
                  )}
                >
                  <span className="text-lg" aria-hidden>
                    {c.flag}
                  </span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className={on ? 'text-brand' : 'text-ink-2'}>{c.dial}</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

/* Re-exported so a page needs one import for the field and its data. */
export { COUNTRIES, type Country }
